import { ref, computed, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from 'axios'
import Vue from 'vue'
import apiEndpoints from '@/constants/apiEndpoints'
import router from '../../../router'

/**
 * Reusable Factory function to generate state and logic for a single table
 * @param {Object} apiParams - Static parameters for the API (e.g., { is_asset: true, is_finished: true })
 * @param {String} defaultSort - Default sort column
 * @param {Ref} fileFilter - Shared attachment filter ('all' | 'missing' | 'complete')
 */
const useTransferTable = (apiParams = {}, defaultSort = 'transfer_number', fileFilter = ref('all')) => {
  const toast = useToast()

  // State
  const tableRef = ref(null)
  const loading = ref(false)
  const perPage = ref(10)
  const currentPage = ref(1)
  const totalRows = ref(0)
  const localItemsCount = ref(0) // ← tracks actual rows returned per fetch
  const searchQuery = ref('')
  const sortBy = ref(defaultSort)
  const isSortDirDesc = ref(true)

  // Computed Meta Data (Showing X to Y of Z entries)
  const dataMeta = computed(() => ({
    from: perPage.value * (currentPage.value - 1) + (localItemsCount.value ? 1 : 0),
    to: perPage.value * (currentPage.value - 1) + localItemsCount.value,
    of: totalRows.value,
  }))

  // Fetch Function
  const fetchData = (ctx, callback) => {
    loading.value = true
    const userToken = Vue.$cookies.get('userToken')
    const offset = perPage.value * (currentPage.value - 1)

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userToken}`,
    }

    const params = {
      search: searchQuery.value,
      order_by: sortBy.value,
      order_sort: isSortDirDesc.value ? 'DESC' : 'ASC',
      limit: perPage.value,
      offset,
      file_filter: fileFilter.value,
      ...apiParams,
    }

    axios
      .get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_TRANSFERS}`, { headers, params })
      .then(response => {
        const data = response.data.data ?? []
        totalRows.value = response.data.data_count ?? 0
        localItemsCount.value = data.length // ← set here, always accurate
        callback(data)
        loading.value = false
      })
      .catch(e => {
        loading.value = false
        localItemsCount.value = 0
        if (e.message && e.message.includes('401')) {
          router.push({ name: 'auth-login' })
        } else {
          console.error(e)
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error fetching data',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        }
      })
  }

  // Refetch helper — calls refresh() method exposed by TransferTable.vue child component
  const refresh = () => {
    if (tableRef.value) tableRef.value.refresh()
  }

  // Watchers to trigger refresh
  watch([currentPage, perPage, searchQuery, sortBy, isSortDirDesc], () => {
    refresh()
  })

  watch(fileFilter, () => {
    currentPage.value = 1
    refresh()
  })

  return {
    tableRef,
    loading,
    perPage,
    currentPage,
    totalRows,
    searchQuery,
    sortBy,
    isSortDirDesc,
    dataMeta,
    fetchData,
    refresh,
  }
}

export default function useMaterialRequestsList() {
  const fileFilter = ref('all')

  // 1. NON-ASSET Tables
  const ongoing = useTransferTable({ is_finished: false, is_asset: false }, 'transfer_number', fileFilter)
  const needsApproval = useTransferTable({ needs_approval: true, is_asset: false }, 'transfer_number', fileFilter)
  const finished = useTransferTable({ is_finished: true, is_asset: false }, 'transfer_number', fileFilter)
  const rejected = useTransferTable({ is_rejected: true, is_asset: false }, 'transfer_number', fileFilter)

  // 2. ASSET Tables
  const assetOngoing = useTransferTable({ is_finished: false, is_asset: true }, 'transfer_number', fileFilter)
  const assetNeedsApproval = useTransferTable({ needs_approval: true, is_asset: true }, 'transfer_number', fileFilter)
  const assetFinished = useTransferTable({ is_finished: true, is_asset: true }, 'transfer_number', fileFilter)
  const assetRejected = useTransferTable({ is_rejected: true, is_asset: true }, 'transfer_number', fileFilter)

  // 3. Global/Common
  const tableColumns = [
    { key: 'transfer_number', sortable: true },
    { key: 'transfer_branch_sender', sortable: true },
    { key: 'transfer_branch_receiver', sortable: true },
    { key: 'transfer_notes', sortable: true },
    { key: 'transfer_created_time', sortable: true },
    { key: 'Action', sortable: true },
  ]

  const roleFilter = ref(null)
  const statusFilter = ref(null)

  const refetchData = () => {
    ongoing.refresh()
    needsApproval.refresh()
    finished.refresh()
    rejected.refresh()
    assetOngoing.refresh()
    assetNeedsApproval.refresh()
    assetFinished.refresh()
    assetRejected.refresh()
  }

  const resolveUserRoleVariant = role => {
    const variants = {
      subscriber: 'primary', author: 'warning', maintainer: 'success', editor: 'info', 'super-admin': 'danger',
    }
    return variants[role] || 'primary'
  }

  const resolveUserRoleIcon = role => {
    const icons = {
      subscriber: 'UserIcon', author: 'SettingsIcon', maintainer: 'DatabaseIcon', editor: 'Edit2Icon', 'super-admin': 'ServerIcon',
    }
    return icons[role] || 'UserIcon'
  }

  const resolveUserStatusVariant = status => {
    const variants = { pending: 'warning', active: 'success', inactive: 'secondary' }
    return variants[status] || 'primary'
  }

  return {
    tableColumns,
    refetchData,
    roleFilter,
    statusFilter,
    fileFilter,
    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolveUserStatusVariant,
    perPageOptions: [10, 25, 50, 100],

    // Non-Asset: Ongoing
    refTransferListTable: ongoing.tableRef,
    loading: ongoing.loading,
    fetchTransfers: ongoing.fetchData,
    perPage: ongoing.perPage,
    currentPage: ongoing.currentPage,
    totalTransfer: ongoing.totalRows,
    searchQuery: ongoing.searchQuery,
    sortBy: ongoing.sortBy,
    isSortDirDesc: ongoing.isSortDirDesc,
    dataMeta: ongoing.dataMeta,

    // Non-Asset: Needs Approval
    refTransferListTableNA: needsApproval.tableRef,
    loadingNA: needsApproval.loading,
    fetchTransfersNA: needsApproval.fetchData,
    perPageNA: needsApproval.perPage,
    currentPageNA: needsApproval.currentPage,
    totalTransferNA: needsApproval.totalRows,
    searchQueryNA: needsApproval.searchQuery,
    sortByNA: needsApproval.sortBy,
    isSortDirDescNA: needsApproval.isSortDirDesc,
    dataMetaNA: needsApproval.dataMeta,

    // Non-Asset: Finished
    refTransferListTableFinished: finished.tableRef,
    loadingFinished: finished.loading,
    fetchTransfersFinished: finished.fetchData,
    perPageFinished: finished.perPage,
    currentPageFinished: finished.currentPage,
    totalTransferFinished: finished.totalRows,
    searchQueryFinished: finished.searchQuery,
    sortByFinished: finished.sortBy,
    isSortDirDescFinished: finished.isSortDirDesc,
    dataMetaFinished: finished.dataMeta,

    // Non-Asset: Rejected
    refTransferListTableRejected: rejected.tableRef,
    loadingRejected: rejected.loading,
    fetchTransfersRejected: rejected.fetchData,
    perPageRejected: rejected.perPage,
    currentPageRejected: rejected.currentPage,
    totalTransferRejected: rejected.totalRows,
    searchQueryRejected: rejected.searchQuery,
    sortByRejected: rejected.sortBy,
    isSortDirDescRejected: rejected.isSortDirDesc,
    dataMetaRejected: rejected.dataMeta,

    // Asset: Ongoing
    refTransferListTableAsset: assetOngoing.tableRef,
    loadingAsset: assetOngoing.loading,
    fetchTransfersAsset: assetOngoing.fetchData,
    perPageAsset: assetOngoing.perPage,
    currentPageAsset: assetOngoing.currentPage,
    totalTransferAsset: assetOngoing.totalRows,
    searchQueryAsset: assetOngoing.searchQuery,
    sortByAsset: assetOngoing.sortBy,
    isSortDirDescAsset: assetOngoing.isSortDirDesc,
    dataMetaAsset: assetOngoing.dataMeta,

    // Asset: Needs Approval
    refTransferListTableAssetNA: assetNeedsApproval.tableRef,
    loadingAssetNA: assetNeedsApproval.loading,
    fetchTransfersAssetNA: assetNeedsApproval.fetchData,
    perPageAssetNA: assetNeedsApproval.perPage,
    currentPageAssetNA: assetNeedsApproval.currentPage,
    totalTransferAssetNA: assetNeedsApproval.totalRows,
    searchQueryAssetNA: assetNeedsApproval.searchQuery,
    sortByAssetNA: assetNeedsApproval.sortBy,
    isSortDirDescAssetNA: assetNeedsApproval.isSortDirDesc,
    dataMetaAssetNA: assetNeedsApproval.dataMeta,

    // Asset: Finished
    refTransferListTableAssetFinished: assetFinished.tableRef,
    loadingAssetFinished: assetFinished.loading,
    fetchTransfersAssetFinished: assetFinished.fetchData,
    perPageAssetFinished: assetFinished.perPage,
    currentPageAssetFinished: assetFinished.currentPage,
    totalTransferAssetFinished: assetFinished.totalRows,
    searchQueryAssetFinished: assetFinished.searchQuery,
    sortByAssetFinished: assetFinished.sortBy,
    isSortDirDescAssetFinished: assetFinished.isSortDirDesc,
    dataMetaAssetFinished: assetFinished.dataMeta,

    // Asset: Rejected
    refTransferListTableAssetRejected: assetRejected.tableRef,
    loadingAssetRejected: assetRejected.loading,
    fetchTransfersAssetRejected: assetRejected.fetchData,
    perPageAssetRejected: assetRejected.perPage,
    currentPageAssetRejected: assetRejected.currentPage,
    totalTransferAssetRejected: assetRejected.totalRows,
    searchQueryAssetRejected: assetRejected.searchQuery,
    sortByAssetRejected: assetRejected.sortBy,
    isSortDirDescAssetRejected: assetRejected.isSortDirDesc,
    dataMetaAssetRejected: assetRejected.dataMeta,
  }
}